import {
  Accordion,
  Button,
  Card,
  Chip,
  ColorArea,
  ColorField,
  ColorPicker,
  ColorSlider,
  ColorSwatch,
  Drawer,
  Input,
  Label,
  ListBox,
  Select,
  Surface,
} from "@heroui/react";
import type { Key } from "@heroui/react";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type {
  PublicMenuLayoutId,
  PublicMenuTheme,
  PublicMenuThemePreset,
} from "../../shared/public-menu-theme";
import {
  PUBLIC_MENU_FONT_FAMILIES,
  PUBLIC_MENU_FONT_WEIGHTS,
  PUBLIC_MENU_PRESETS,
  contrastRatio,
  getReadableForeground,
  resolvePublicTheme,
} from "../../shared/public-menu-theme";
import { PublicMenuNavbar } from "./PublicMenuNavbar";
import {
  PublicMenuThemeProvider,
  usePublicMenuTheme,
} from "./PublicMenuThemeProvider";

type ColorToken = keyof PublicMenuTheme["colors"];

interface PublicMenuThemeEditorProps {
  draft: PublicMenuTheme;
  onChange(theme: PublicMenuTheme): void;
  onBlockingIssue(issue: string | null): void;
  businessName: string;
  description: string;
  logoUrl: string | null;
  currencySymbol: string;
}

const HEX_COLOR = /^#[0-9A-F]{6}$/;
const BASIC_COLOR_TOKENS = [
  "background",
  "surface",
  "primary",
  "secondary",
  "accent",
  "textPrimary",
  "price",
  "promo",
] as const satisfies readonly ColorToken[];
const ADVANCED_COLOR_TOKENS = [
  "surfaceAlt",
  "primaryForeground",
  "secondaryForeground",
  "accentForeground",
  "textSecondary",
  "textMuted",
  "border",
  "success",
  "danger",
  "warning",
] as const satisfies readonly ColorToken[];
const COLOR_LABELS: Record<ColorToken, string> = {
  background: "Fondo",
  surface: "Superficie",
  surfaceAlt: "Superficie alternativa",
  primary: "Primario",
  primaryForeground: "Texto sobre primario",
  secondary: "Secundario",
  secondaryForeground: "Texto sobre secundario",
  accent: "Acento",
  accentForeground: "Texto sobre acento",
  textPrimary: "Texto principal",
  textSecondary: "Texto secundario",
  textMuted: "Texto atenuado",
  border: "Bordes",
  price: "Precios",
  promo: "Promociones",
  success: "Éxito",
  danger: "Error",
  warning: "Advertencia",
};

const PRESET_CATEGORIES: ReadonlyArray<{
  label: string;
  ids: ReadonlyArray<string>;
}> = [
  { label: "Clásicos", ids: ["modern-light", "minimal", "vibrant-brand"] },
  { label: "Marcas", ids: ["wingers-diner", "dinner-king", "bold-burger-red"] },
  {
    label: "Modo oscuro",
    ids: ["elegant-dark", "neon-night", "velvet-luxury"],
  },
  {
    label: "Cálidos",
    ids: [
      "warm-restaurant",
      "golden-yellow",
      "citrus-orange",
      "espresso-vintage",
    ],
  },
  {
    label: "Color y frescura",
    ids: ["crimson-red", "organic-green", "caribbean-breeze"],
  },
];

const PUBLIC_MENU_LAYOUT_LABELS: Record<PublicMenuLayoutId, string> = {
  default: "Predeterminado",
  wingers: "WINGER'S",
  dinnerking: "Dinner King",
};

interface SelectOption<T extends string | number> {
  value: T;
  label: string;
}

function ThemeSelect<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly SelectOption<T>[];
  onChange(value: T): void;
}) {
  return (
    <Select
      fullWidth
      value={String(value)}
      variant="secondary"
      onChange={(key: Key | null) => {
        const selected = options.find(
          (option) => String(option.value) === String(key),
        );
        if (selected) onChange(selected.value);
      }}
    >
      <Label>{label}</Label>
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {options.map((option) => (
            <ListBox.Item
              key={String(option.value)}
              id={String(option.value)}
              textValue={option.label}
            >
              {option.label}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}

function EditorSection({
  id,
  number,
  title,
  children,
}: {
  id: string;
  number: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <Accordion.Item id={id}>
      <Accordion.Heading>
        <Accordion.Trigger className="font-bold">
          <span className="mr-3 grid size-7 shrink-0 place-items-center rounded-full bg-accent-soft text-xs text-accent-soft-foreground">
            {number}
          </span>
          {title}
          <Accordion.Indicator />
        </Accordion.Trigger>
      </Accordion.Heading>
      <Accordion.Panel>
        <Accordion.Body className="pb-5">{children}</Accordion.Body>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

function ColorControl({
  token,
  value,
  presetValue,
  onChange,
}: {
  token: ColorToken;
  value: string;
  presetValue: string;
  onChange(value: string): void;
}) {
  const normalized = value.trim().toUpperCase();
  const valid = HEX_COLOR.test(normalized);
  return (
    <div className="grid gap-2 rounded-xl border border-border bg-surface p-3">
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={`theme-color-${token}`}
          className="text-sm font-semibold text-foreground"
        >
          {COLOR_LABELS[token]}
        </label>
        <Button size="sm" variant="ghost" onPress={() => onChange(presetValue)}>
          Restaurar
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <ColorPicker
          value={valid ? normalized : presetValue}
          onChange={(color) => onChange(color.toString("hex").toUpperCase())}
        >
          <ColorPicker.Trigger
            aria-label={`Elegir ${COLOR_LABELS[token]}`}
            className="shrink-0"
          >
            <ColorSwatch size="lg" />
          </ColorPicker.Trigger>
          <ColorPicker.Popover className="gap-3">
            <ColorArea
              aria-label={`Saturación y brillo de ${COLOR_LABELS[token]}`}
              className="max-w-full"
              colorSpace="hsb"
              xChannel="saturation"
              yChannel="brightness"
            >
              <ColorArea.Thumb />
            </ColorArea>
            <ColorSlider
              aria-label={`Tono de ${COLOR_LABELS[token]}`}
              channel="hue"
              colorSpace="hsb"
            >
              <ColorSlider.Track>
                <ColorSlider.Thumb />
              </ColorSlider.Track>
            </ColorSlider>
            <ColorField aria-label={`Hexadecimal de ${COLOR_LABELS[token]}`}>
              <ColorField.Group variant="secondary">
                <ColorField.Prefix>
                  <ColorSwatch size="xs" />
                </ColorField.Prefix>
                <ColorField.Input />
              </ColorField.Group>
            </ColorField>
          </ColorPicker.Popover>
        </ColorPicker>
        <Input
          id={`theme-color-${token}`}
          aria-label={`${COLOR_LABELS[token]} hexadecimal`}
          className="h-10 min-w-0 flex-1 border border-border bg-surface px-3 font-mono text-sm uppercase"
          maxLength={7}
          value={value}
          variant="secondary"
          onChange={(event) => onChange(event.target.value.toUpperCase())}
        />
      </div>
      {!valid && (
        <p className="text-xs font-semibold text-danger">
          Usa el formato #RRGGBB.
        </p>
      )}
    </div>
  );
}

function ContrastStatus({
  label,
  background,
  foreground,
  blocking = false,
}: {
  label: string;
  background: string;
  foreground: string;
  blocking?: boolean;
}) {
  const valid = HEX_COLOR.test(background) && HEX_COLOR.test(foreground);
  const ratio = valid ? contrastRatio(background, foreground) : 0;
  const warning = !valid || ratio < 4.5;
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-xs ${warning ? (blocking ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning") : "bg-success-soft text-success"}`}
    >
      <span className="font-semibold">{label}</span>
      <span className="font-black">
        {valid ? `${ratio.toFixed(2)}:1` : "Color inválido"}
      </span>
    </div>
  );
}

function MenuPreview({
  theme,
  businessName,
  description,
  logoUrl,
  currencySymbol,
}: Omit<
  PublicMenuThemeEditorProps,
  "draft" | "onChange" | "onBlockingIssue"
> & { theme: PublicMenuTheme }) {
  const cartClass =
    theme.appearance.cartStyle === "surfaceAlt"
      ? "menu-panel-alt"
      : "menu-panel";
  return (
    <div className="menu-text-primary min-h-full bg-[var(--menu-background)]">
      <PublicMenuNavbar
        name={businessName || "Mi restaurante"}
        subtitle="Menú para pedir"
        logoUrl={logoUrl ?? undefined}
        navbarStyle={theme.appearance.navbarStyle}
        trailing={
          <Chip size="sm" variant="soft">
            2 productos
          </Chip>
        }
      />

      <div className="menu-density-section grid gap-4 p-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[var(--menu-accent)]">
            Carta del día
          </p>
          <h2 className="menu-text-primary mt-1 text-2xl font-black">
            Sabores que provocan
          </h2>
          <p className="menu-text-secondary mt-2 text-sm leading-5">
            {description || "Preparado al momento con ingredientes frescos."}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Chip color="accent" size="sm">
            Favoritos
          </Chip>
          <Chip size="sm" variant="soft">
            Hamburguesas
          </Chip>
          <Chip size="sm" variant="soft">
            Bebidas
          </Chip>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Card
            className="menu-product-card menu-panel overflow-hidden border p-0"
            variant="default"
          >
            <Card.Content>
              <Surface
                className="grid aspect-[4/3] place-items-center rounded-none text-3xl text-[var(--menu-accent)]"
                variant="tertiary"
              >
                ○
              </Surface>
            </Card.Content>
            <Card.Header className="px-3 pt-3">
              <Card.Title className="menu-text-primary text-sm font-black">
                Burger clásica
              </Card.Title>
              <Card.Description className="menu-text-secondary line-clamp-2 text-xs">
                Carne, queso y salsa de la casa.
              </Card.Description>
            </Card.Header>
            <Card.Content className="px-3">
              <strong className="menu-price text-base font-black">$8.90</strong>
            </Card.Content>
            <Card.Footer className="px-3 pb-3 pt-2">
              <Button fullWidth className="menu-critical-cta" size="sm">
                Elegir
              </Button>
            </Card.Footer>
          </Card>

          <Card
            className="menu-product-card menu-panel overflow-hidden border p-0"
            variant="default"
          >
            <Card.Content className="relative">
              <Surface
                className="grid aspect-[4/3] place-items-center rounded-none text-3xl text-[var(--menu-accent)]"
                variant="tertiary"
              >
                ○
              </Surface>
              <span className="menu-promo absolute left-2 top-2 rounded-full px-2 py-1 text-[10px] font-black">
                PROMO
              </span>
            </Card.Content>
            <Card.Header className="px-3 pt-3">
              <Card.Title className="menu-text-primary text-sm font-black">
                Combo especial
              </Card.Title>
              <Card.Description className="menu-text-secondary line-clamp-2 text-xs">
                Burger, papas y bebida.
              </Card.Description>
            </Card.Header>
            <Card.Content className="px-3">
              <strong className="menu-price text-base font-black">
                $11.50
              </strong>
            </Card.Content>
            <Card.Footer className="px-3 pb-3 pt-2">
              <Button fullWidth size="sm" variant="secondary">
                Ver combo
              </Button>
            </Card.Footer>
          </Card>
        </div>

        <Surface
          className={`${cartClass} rounded-[var(--menu-radius)] border border-border p-4`}
          variant="default"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="menu-text-muted text-[10px] font-black uppercase tracking-widest">
                Tu pedido
              </p>
              <p className="menu-text-primary mt-1 text-sm font-bold">
                2 productos
              </p>
            </div>
            <strong className="menu-price text-lg font-black">
              {currencySymbol || "Bs"} 816,00
            </strong>
          </div>
          <Button fullWidth className="menu-critical-cta mt-3" size="sm">
            Revisar carrito
          </Button>
        </Surface>
      </div>
    </div>
  );
}

function MobilePreviewDrawer({
  open,
  onOpenChange,
  children,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  children: ReactNode;
}) {
  const { portalContainer } = usePublicMenuTheme();
  return (
    <Drawer.Backdrop
      UNSTABLE_portalContainer={portalContainer ?? undefined}
      isOpen={open}
      onOpenChange={onOpenChange}
      variant="blur"
    >
      <Drawer.Content placement="bottom">
        <Drawer.Dialog className="menu-panel max-h-[92dvh] w-full">
          <Drawer.Handle />
          <Drawer.CloseTrigger aria-label="Cerrar vista previa" />
          <Drawer.Header className="menu-panel border-b border-border px-5 py-4">
            <Drawer.Heading className="menu-text-primary font-black">
              Vista previa del menú
            </Drawer.Heading>
          </Drawer.Header>
          <Drawer.Body className="menu-panel p-0">{children}</Drawer.Body>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
}

export function PublicMenuThemeEditor({
  draft,
  onChange,
  onBlockingIssue,
  businessName,
  description,
  logoUrl,
  currencySymbol,
}: PublicMenuThemeEditorProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const activePreset =
    PUBLIC_MENU_PRESETS.find((preset) => preset.id === draft.presetId) ??
    PUBLIC_MENU_PRESETS[0];
  const invalidToken =
    (Object.entries(draft.colors) as Array<[ColorToken, string]>).find(
      ([, value]) => !HEX_COLOR.test(value),
    )?.[0] ?? null;
  const primaryContrast =
    HEX_COLOR.test(draft.colors.primary) &&
    HEX_COLOR.test(draft.colors.primaryForeground)
      ? contrastRatio(draft.colors.primary, draft.colors.primaryForeground)
      : 0;

  useEffect(() => {
    if (invalidToken) {
      onBlockingIssue(
        `${COLOR_LABELS[invalidToken]} debe usar el formato #RRGGBB.`,
      );
    } else if (primaryContrast < 4.5) {
      onBlockingIssue(
        "El botón principal necesita un contraste mínimo de 4.5:1.",
      );
    } else {
      onBlockingIssue(null);
    }
  }, [invalidToken, onBlockingIssue, primaryContrast]);

  const updateColor = (token: ColorToken, value: string) => {
    const colors = { ...draft.colors, [token]: value };
    const normalized = value.trim().toUpperCase();
    if (HEX_COLOR.test(normalized)) {
      if (token === "primary")
        colors.primaryForeground = getReadableForeground(normalized);
      if (token === "secondary")
        colors.secondaryForeground = getReadableForeground(normalized);
      if (token === "accent")
        colors.accentForeground = getReadableForeground(normalized);
    }
    onChange({ ...draft, colors });
  };

  const updateTypography = <K extends keyof PublicMenuTheme["typography"]>(
    key: K,
    value: PublicMenuTheme["typography"][K],
  ) => {
    onChange({ ...draft, typography: { ...draft.typography, [key]: value } });
  };
  const updateAppearance = <K extends keyof PublicMenuTheme["appearance"]>(
    key: K,
    value: PublicMenuTheme["appearance"][K],
  ) => {
    onChange({ ...draft, appearance: { ...draft.appearance, [key]: value } });
  };

  const previewProps = {
    theme: draft,
    businessName,
    description,
    logoUrl,
    currencySymbol,
  };
  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
      <div className="rounded-xl border border-border bg-surface px-4 py-3 text-sm lg:col-span-2">
        Diseño asignado: <strong>{PUBLIC_MENU_LAYOUT_LABELS[draft.layoutId]}</strong>.
        Los presets de esta pantalla cambian colores y fuentes, no la estructura asignada.
        La vista previa muestra los estilos; abre el menú público para ver el layout completo.
      </div>
      <Accordion
        allowsMultipleExpanded
        defaultExpandedKeys={["quick", "colors"]}
        className="min-w-0"
        variant="surface"
      >
        <EditorSection id="quick" number={1} title="Tema rápido">
          <div className="grid gap-4">
            {PRESET_CATEGORIES.map((category) => {
              const presets = category.ids
                .map((id) =>
                  PUBLIC_MENU_PRESETS.find((preset) => preset.id === id),
                )
                .filter((preset): preset is PublicMenuThemePreset =>
                  Boolean(preset),
                );
              if (presets.length === 0) return null;
              return (
                <div key={category.label} className="grid gap-2">
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-muted">
                    {category.label}
                  </p>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {presets.map((preset) => {
                      const active = draft.presetId === preset.id;
                      const colors = preset.theme.colors;
                      return (
                        <button
                          key={preset.id}
                          type="button"
                          title={preset.name}
                          aria-pressed={active}
                          onClick={() =>
                            onChange(
                              resolvePublicTheme({
                                ...preset.theme,
                                layoutId: draft.layoutId,
                                layoutConfig: draft.layoutConfig,
                              }),
                            )
                          }
                          className={`rounded-xl border p-2 text-left transition ${
                            active
                              ? "border-primary bg-surface shadow-md ring-2 ring-primary"
                              : "border-border bg-surface hover:border-foreground/40 hover:bg-surface-secondary"
                          }`}
                        >
                          <span
                            className="relative block w-full overflow-hidden rounded-lg border border-black/5 p-1.5"
                            style={{ background: colors.background }}
                          >
                            <span
                              className="block h-2 rounded-sm"
                              style={{ background: colors.primary }}
                            />
                            <span
                              className="mt-1 block h-1 w-3/4 rounded-sm"
                              style={{ background: colors.textPrimary }}
                            />
                            <span
                              className="mt-0.5 block h-1 w-1/2 rounded-sm"
                              style={{ background: colors.textSecondary }}
                            />
                            <span
                              className="mt-1.5 block rounded-sm py-0.5 text-center text-[7px] font-black leading-none"
                              style={{
                                background: colors.primary,
                                color: colors.primaryForeground,
                              }}
                            >
                              Pedir
                            </span>
                            {active && (
                              <span className="absolute right-1 top-1 grid size-4 place-items-center rounded-full bg-foreground text-[9px] font-black text-background shadow">
                                ✓
                              </span>
                            )}
                          </span>
                          <span className="mt-1.5 block truncate text-[11px] font-bold text-foreground">
                            {preset.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </EditorSection>

        <EditorSection id="colors" number={2} title="Colores">
          <div className="grid gap-3 sm:grid-cols-2">
            {BASIC_COLOR_TOKENS.map((token) => (
              <ColorControl
                key={token}
                token={token}
                value={draft.colors[token]}
                presetValue={activePreset.theme.colors[token]}
                onChange={(value) => updateColor(token, value)}
              />
            ))}
          </div>

          <Accordion
            className="mt-4 rounded-xl border border-border"
            variant="surface"
          >
            <Accordion.Item id="advanced-colors">
              <Accordion.Heading>
                <Accordion.Trigger className="font-bold">
                  Colores avanzados
                  <Accordion.Indicator />
                </Accordion.Trigger>
              </Accordion.Heading>
              <Accordion.Panel>
                <Accordion.Body>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {ADVANCED_COLOR_TOKENS.map((token) => (
                      <ColorControl
                        key={token}
                        token={token}
                        value={draft.colors[token]}
                        presetValue={activePreset.theme.colors[token]}
                        onChange={(value) => updateColor(token, value)}
                      />
                    ))}
                  </div>
                </Accordion.Body>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          <div className="mt-4 grid gap-2">
            <p className="text-sm font-black text-foreground">
              Contrastes críticos
            </p>
            <ContrastStatus
              label="Fondo / texto principal"
              background={draft.colors.background}
              foreground={draft.colors.textPrimary}
            />
            <ContrastStatus
              label="Superficie / texto principal"
              background={draft.colors.surface}
              foreground={draft.colors.textPrimary}
            />
            <ContrastStatus
              blocking
              label="Primario / texto"
              background={draft.colors.primary}
              foreground={draft.colors.primaryForeground}
            />
            <ContrastStatus
              label="Secundario / texto"
              background={draft.colors.secondary}
              foreground={draft.colors.secondaryForeground}
            />
            <ContrastStatus
              label="Acento / texto"
              background={draft.colors.accent}
              foreground={draft.colors.accentForeground}
            />
          </div>
        </EditorSection>

        <EditorSection id="typography" number={3} title="Tipografía">
          <div className="grid gap-4 sm:grid-cols-2">
            <ThemeSelect
              label="Fuente de títulos"
              value={draft.typography.headingFont}
              options={PUBLIC_MENU_FONT_FAMILIES.map((value) => ({
                value,
                label: value,
              }))}
              onChange={(value) => updateTypography("headingFont", value)}
            />
            <ThemeSelect
              label="Fuente del cuerpo"
              value={draft.typography.bodyFont}
              options={PUBLIC_MENU_FONT_FAMILIES.map((value) => ({
                value,
                label: value,
              }))}
              onChange={(value) => updateTypography("bodyFont", value)}
            />
            <ThemeSelect
              label="Peso de títulos"
              value={draft.typography.headingWeight}
              options={PUBLIC_MENU_FONT_WEIGHTS.map((value) => ({
                value,
                label: String(value),
              }))}
              onChange={(value) => updateTypography("headingWeight", value)}
            />
            <ThemeSelect
              label="Peso del cuerpo"
              value={draft.typography.bodyWeight}
              options={PUBLIC_MENU_FONT_WEIGHTS.map((value) => ({
                value,
                label: String(value),
              }))}
              onChange={(value) => updateTypography("bodyWeight", value)}
            />
            <ThemeSelect
              label="Escala"
              value={draft.typography.scale}
              options={[
                { value: "compact", label: "Compacta" },
                { value: "normal", label: "Normal" },
                { value: "large", label: "Grande" },
              ]}
              onChange={(value) => updateTypography("scale", value)}
            />
          </div>
        </EditorSection>

        <EditorSection id="components" number={4} title="Tarjetas y botones">
          <div className="grid gap-4 sm:grid-cols-2">
            <ThemeSelect
              label="Radio"
              value={draft.appearance.borderRadius}
              options={[
                { value: "small", label: "Pequeño" },
                { value: "medium", label: "Medio" },
                { value: "large", label: "Grande" },
              ]}
              onChange={(value) => updateAppearance("borderRadius", value)}
            />
            <ThemeSelect
              label="Tarjetas"
              value={draft.appearance.cardStyle}
              options={[
                { value: "flat", label: "Planas" },
                { value: "bordered", label: "Con borde" },
                { value: "elevated", label: "Elevadas" },
              ]}
              onChange={(value) => updateAppearance("cardStyle", value)}
            />
            <ThemeSelect
              label="Botones"
              value={draft.appearance.buttonStyle}
              options={[
                { value: "solid", label: "Sólidos" },
                { value: "soft", label: "Suaves" },
                { value: "outline", label: "Contorno" },
              ]}
              onChange={(value) => updateAppearance("buttonStyle", value)}
            />
            <ThemeSelect
              label="Densidad"
              value={draft.appearance.density}
              options={[
                { value: "compact", label: "Compacta" },
                { value: "comfortable", label: "Cómoda" },
              ]}
              onChange={(value) => updateAppearance("density", value)}
            />
            <ThemeSelect
              label="Barra superior"
              value={draft.appearance.navbarStyle}
              options={[
                { value: "clear", label: "Clara" },
                { value: "primary", label: "Primaria" },
                { value: "dark", label: "Oscura" },
              ]}
              onChange={(value) => updateAppearance("navbarStyle", value)}
            />
            <ThemeSelect
              label="Mini carrito"
              value={draft.appearance.cartStyle}
              options={[
                { value: "surface", label: "Superficie" },
                { value: "surfaceAlt", label: "Superficie alternativa" },
              ]}
              onChange={(value) => updateAppearance("cartStyle", value)}
            />
            <ThemeSelect
              label="Chat público"
              value={draft.appearance.chatStyle}
              options={[
                { value: "surface", label: "Superficie" },
                { value: "surfaceAlt", label: "Superficie alternativa" },
                { value: "tinted", label: "Teñido con marca" },
              ]}
              onChange={(value) => updateAppearance("chatStyle", value)}
            />
            <ThemeSelect
              label="Configurador"
              value={draft.appearance.configuratorStyle}
              options={[
                { value: "surface", label: "Superficie" },
                { value: "surfaceAlt", label: "Superficie alternativa" },
              ]}
              onChange={(value) => updateAppearance("configuratorStyle", value)}
            />
          </div>
        </EditorSection>
      </Accordion>

      <aside className="hidden lg:block">
        <div className="sticky top-6 overflow-hidden rounded-2xl border border-border bg-surface shadow-surface">
          <div className="border-b border-border px-4 py-3">
            <p className="font-black text-foreground">Vista previa</p>
            <p className="text-xs text-muted">
              Los cambios se muestran al instante.
            </p>
          </div>
          <PublicMenuThemeProvider
            theme={draft}
            className="max-h-[calc(100vh-10rem)] min-h-0 overflow-y-auto"
          >
            <MenuPreview {...previewProps} />
          </PublicMenuThemeProvider>
        </div>
      </aside>

      <div className="lg:hidden">
        <button
          type="button"
          aria-label="Abrir vista previa del menú"
          className="fixed bottom-24 right-4 z-30 rounded-full bg-accent px-4 py-3 text-sm font-black text-accent-foreground shadow-[0_18px_40px_rgba(15,23,42,0.24)] transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          onClick={() => setPreviewOpen(true)}
        >
          Vista previa
        </button>
        <PublicMenuThemeProvider theme={draft} className="min-h-0">
          <MobilePreviewDrawer open={previewOpen} onOpenChange={setPreviewOpen}>
            <MenuPreview {...previewProps} />
          </MobilePreviewDrawer>
        </PublicMenuThemeProvider>
      </div>
    </div>
  );
}
